import api from "utils/api";
import endpoint from "utils/endpoint";
import { SeoPreferences } from "domain/model/SeoPreferences";
import dayjs from "dayjs";

type Post = {
  postContent: {
    title: string;
    content: string;
    postDate: Date;
    subcategory: string;
    slug: string;
    thumbnailUrl: string | null;
  };
  seoPreferences: SeoPreferences;
};

class ArticleRepository {
  async applyArticleTemplate(isAgency: boolean, storeId: string | undefined) {
    const body = {
      apply_template: {
        category: "article",
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.article.applyTemplate(storeId)
        : endpoint.store.article.applyTemplate();

    return api.put(url, body);
  }

  addNewPost(
    isAgency: boolean,
    storeId: string | undefined,
    { postContent, seoPreferences }: Post,
    isDraft: boolean
  ) {
    const article = {
      title: postContent.title,
      content: postContent.content,
      created_at: dayjs(postContent.postDate).unix(),
      subcategory: postContent.subcategory,
      slug: postContent.slug,
      thumbnail_url: postContent.thumbnailUrl,
      is_draft: isDraft,
    };
    const body = {
      article,
      seo_tag: {
        ...seoPreferences,
        og: {
          ...seoPreferences.og,
          site_name: seoPreferences.og.siteName,
        },
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.article.root(storeId)
        : endpoint.store.article.root();

    return api.post(url, body).catch((error) => {
      if (error?.response?.data?.error === undefined) {
        return Promise.reject("error");
      }

      return Promise.reject(error.response.data.error);
    });
  }

  updateArticle(
    isAgency: boolean,
    storeId: string | undefined,
    filePath: string,
    { postContent, seoPreferences }: Post,
    isDraft: boolean
  ) {
    const article = {
      title: postContent.title,
      content: postContent.content,
      created_at: dayjs(postContent.postDate).unix(),
      subcategory: postContent.subcategory,
      slug: postContent.slug,
      thumbnail_url: postContent.thumbnailUrl,
      filepath: filePath,
      is_draft: isDraft,
    };
    const body = {
      article,
      seo_tag: {
        ...seoPreferences,
        og: {
          ...seoPreferences.og,
          site_name: seoPreferences.og.siteName,
        },
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.article.root(storeId)
        : endpoint.store.article.root();

    return api.put(url, body).catch((error) => {
      if (error?.response?.data?.error === undefined) {
        return Promise.reject("error");
      }

      return Promise.reject(error.response.data.error);
    });
  }

  deletePost(isAgency: boolean, storeId: string | undefined, filePath: string) {
    const url =
      isAgency && storeId
        ? endpoint.agency.article.root(storeId)
        : endpoint.store.article.root();
    return api.delete(url, {
      params: {
        filepath: filePath,
      },
    });
  }

  createTemplate(
    isAgency: boolean,
    storeId: string | undefined,
    content: string
  ) {
    const body = {
      template: {
        label: "article",
        text: content,
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.article.template(storeId)
        : endpoint.store.article.template();

    return api.post(url, body).catch((error) => {
      if (error?.response?.data?.errors?.content === undefined) {
        return Promise.reject([]);
      }

      return Promise.reject(error.response.data.errors.content);
    });
  }

  updateTemplate(
    isAgency: boolean,
    storeId: string | undefined,
    content: string
  ) {
    const body = {
      template: {
        label: "article",
        text: content,
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.article.template(storeId)
        : endpoint.store.article.template();

    return api.put(url, body).catch((error) => {
      if (error?.response?.data?.errors?.content === undefined) {
        return Promise.reject([]);
      }

      return Promise.reject(error.response.data.errors.content);
    });
  }

  async addArticleCategory(
    isAgency: boolean,
    storeId: string | undefined,
    newCategoryName: string
  ) {
    const body = {
      subcategory: {
        category: "articles",
        subcategory_name: newCategoryName,
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.subcategory.root(storeId)
        : endpoint.store.subcategory.root();

    return api.post(url, body);
  }

  async updateCategory(
    isAgency: boolean,
    storeId: string | undefined,
    categoryValue: string,
    categoryName: string
  ) {
    const body = {
      subcategory: {
        category: "articles",
        subcategory_name: categoryName,
        subcategory_value: categoryValue,
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.subcategory.root(storeId)
        : endpoint.store.subcategory.root();
    return api.put(url, body);
  }

  async deleteCategory(
    isAgency: boolean,
    storeId: string | undefined,
    categoryValue: string
  ) {
    const body = {
      subcategory: {
        category: "articles",
        subcategory_value: categoryValue,
      },
    };
    const url =
      isAgency && storeId
        ? endpoint.agency.subcategory.root(storeId)
        : endpoint.store.subcategory.root();
    return api.delete(url, {
      data: body,
    });
  }
}

export default new ArticleRepository();
